import { defineStore } from "pinia";
import { createClient } from "@/sdk";
import type {
  WebhusetConstantsOrderAgreementPeriodTypeItem,
  WebhusetOrderApiFeaturesCatalogMicrosoft365GetResponse_CatalogMicrosoft365ItemResultRestDto,
} from "~/sdk/generated/models";
import { DefaultApiError } from "@microsoft/kiota-abstractions";

export const useMicrosoft365CatalogStore = defineStore(
  "microsoft-365-catalog",
  () => {
    const cartStore = useCartStore();
    const config = useRuntimeConfig();

    const client = createClient(config.public.apiServer);

    const isReady = ref(false);
    const catalog =
      ref<
        WebhusetOrderApiFeaturesCatalogMicrosoft365GetResponse_CatalogMicrosoft365ItemResultRestDto[]
      >();

    watch(
      () => cartStore.coupon,
      () => {
        isReady.value = false;
        console.log("Coupon changed", cartStore.coupon);
      }
    );

    const fetchCatalog = async () => {
      try {
        const microsoft365 = await client.catalog.microsoft365.get({
          queryParameters: {
            coupon: cartStore.coupon,
          },
        });

        catalog.value = microsoft365?.products;
        isReady.value = true;
      } catch (err) {
        if (err instanceof DefaultApiError && err.responseStatusCode === 404) {
          await nextTick(() => {
            showError("Ugyldig kupongkode");
          });
        } else {
          console.error("Error loading M365 catalog", err);
          await nextTick(() => {
            showError("En uventet feil oppstod under henting av produkter");
          });
        }
      }
    };

    watch(
      isReady,
      async () => {
        if (!isReady.value) {
          await fetchCatalog();
        }
      },
      {
        immediate: true,
      }
    );

    return {
      isReady,
      catalog,
      getSubscriptionById: (subscriptionId: number) => {
        return catalog.value
          ?.flatMap((m365) => m365.subscriptions)
          .find((sub) => sub?.subscriptionId === subscriptionId);
      },
      getPackageBySubscriptionId: (subscriptionId: number) => {
        return catalog.value?.find((pkg) =>
          pkg.subscriptions?.some((s) => s.subscriptionId === subscriptionId)
        );
      },
      getPackageSubscriptionsByAgreementPeriod: (
        agreementPeriod: WebhusetConstantsOrderAgreementPeriodTypeItem
      ) => {
        return catalog.value?.map((pkg) => {
          const clone = { ...pkg };
          clone.subscriptions = pkg.subscriptions?.filter(
            (s) => s.agreementPeriodType === agreementPeriod
          );
          return clone;
        });
      },
    };
  }
);
